import { useMemo, useState } from 'react';
import { BrowseResults } from '../../components/BrowseResults';
import type { TagFilter } from '../../components/TagFilterBuilder';
import { PageHeader } from '../../components/PageHeader';
import { Breadcrumb } from '../../components/Breadcrumb';
import { BatchProgressBar } from '../../components/BatchProgressBar';
import { getRouteParams } from '../../services/angular-bridge';
import { loadBatchTagFilter, saveBatchTagFilter } from '../../utilities/batchTagFilter';
import { useTagByName, useTagsByIds } from '../../hooks/useProjectTags';
import { useProject } from '../../hooks/useProject';
import { useProjectPageMount } from '../../hooks/useProjectPageMount';
import { useBatchReviewMetrics } from '../../hooks/useReview';

/**
 * BrowseBatchPage — simplified batch review (/home/batch/:batchId[/:reviewState]).
 * The Tag Explorer scoped to a batch: the filter is derived from the batch tag id and
 * the review state, mirroring BrowseController's simplified-review filter:
 *   - (none)       → resources tagged with the batch
 *   - reviewed     → batch AND the "Reviewed" tag
 *   - notReviewed  → batch AND NOT "Reviewed"
 * The toolbar tag filter narrows that scope further (merged in; batch scope not editable)
 * and is saved, carrying across batches (reset on project change, not shared with Tag Explorer).
 *
 * The nav "active batch" highlight (Angular's browse.batch.selected broadcast) and the
 * simplified-review chrome are deferred (nav is Wave 4; grid chrome is M3).
 */
export function BrowseBatchPage() {
  const { projectId, batchId, reviewState } = getRouteParams();
  const needsReviewedTag = reviewState === 'reviewed' || reviewState === 'notReviewed';
  const { data: reviewedTag, isLoading: loadingReviewedTag } = useTagByName(
    projectId,
    needsReviewedTag ? 'Reviewed' : null
  );
  const { data: project } = useProject(projectId);
  useProjectPageMount(project, 'projects');
  // The batch is a tag; resolve its name for the breadcrumb.
  const { data: batchTags } = useTagsByIds(projectId, [batchId]);
  const batchName = batchTags?.[0]?.name ?? 'Batch';
  const reviewLabel =
    reviewState === 'reviewed' ? 'Reviewed' : reviewState === 'notReviewed' ? 'Not Reviewed' : null;
  const { data: metrics } = useBatchReviewMetrics(projectId, batchId);
  const [userFilter, setUserFilter] = useState<TagFilter | null>(() => loadBatchTagFilter(projectId));
  const applyUserFilter = (f: TagFilter) => {
    const next = f.size > 0 ? f : null;
    saveBatchTagFilter(projectId, next);
    setUserFilter(next);
  };

  const chrome = (
    <>
      <PageHeader icon="far fa-hdd" title={project?.name ?? 'Project'} />
      <Breadcrumb
        items={[
          { label: 'Projects', href: '/project' },
          { label: project?.name ?? 'Project', href: `/project/${projectId}/collections` },
          { label: 'Batch' },
          { label: batchName },
          ...(reviewLabel ? [{ label: reviewLabel }] : []),
        ]}
      />
      {metrics && <BatchProgressBar reviewed={metrics.reviewed} notReviewed={metrics.notReviewed} />}
    </>
  );

  const filter = useMemo(() => {
    const and = [batchId, ...(userFilter?.tags.and ?? [])];
    const or = userFilter?.tags.or ?? [];
    const not = [...(userFilter?.tags.not ?? [])];
    if (reviewState === 'reviewed' && reviewedTag) and.push(reviewedTag.id);
    if (reviewState === 'notReviewed' && reviewedTag) not.push(reviewedTag.id);
    return { size: and.length + or.length + not.length, projectId, tags: { and, or, not } };
  }, [projectId, batchId, reviewState, reviewedTag, userFilter]);

  // Wait for the Reviewed tag before building a reviewed/notReviewed filter, so we
  // don't briefly show the whole batch with the wrong scope.
  if (needsReviewedTag && loadingReviewedTag) {
    return (
      <div className="container-fluid">
        {chrome}
        <p className="text-muted">Loading…</p>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {chrome}
      <BrowseResults
        projectId={projectId}
        filter={filter}
        editableFilter={userFilter}
        onFilterChange={applyUserFilter}
        testId="batch-home-result-grid"
      />
    </div>
  );
}
